import Button from "components/base/button/Button";
import { FC, RefObject, useCallback, useContext, useRef } from "react";
import classNames from "classnames";
import { ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import Img from "components/base/img/Img";
import { AssetsDirectory } from "@finbackoffice/enums";
import Link from "next/link";
import {
    AuthContext,
    ChatServiceContext,
    CpfSignupProvider,
    useLayoutProfileConfig,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { usePathnameWithLocal } from "hooks";
import {
    CrmContext,
    IModalForwardRefPropsV2,
    ModalsContextV2,
    UserAccountContextV2,
} from "contexts";
import { ModalTypes, RouteNames } from "utils/constants";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import WalletSelector from "components/header/wallet-selector/WalletSelector";
import SignupModal from "components/account/signup/signup-modal/SignupModal";
import { HeaderContext } from "components/header/Header";
import Popover from "components/base/popover/Popover";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import AccountPopover from "components/account/menu/popover/AccountPopover";
import { SignupInputFieldName } from "@finbackoffice/enums";
import ActiveLink from "components/base/active-link/ActiveLink";
import { useSignupConfigV2 } from "hooks";
import styles from "./tablet.module.sass";
import HeaderNav from "./nav/HeaderNav";

const UserPanel: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { pathnameValues } = usePathnameWithLocal();
    const { onWalletOpen } = useContext(HeaderContext);
    const { showWidget, visible } = useContext(ChatServiceContext);
    const { unreadMessagesCount, openInbox } = useContext(CrmContext);
    const { userEmail, username, isUserVerified } = useContext(UserAccountContextV2);
    const { config: referralConfig } = useLayoutProfileConfig(ProfileLayoutSectionEnum.Referral);
    const { config: messagesConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Messages,
        undefined,
        "profileTablet",
    );
    const popoverTriggerRef = useRef<HTMLButtonElement>(null);
    const { signupConfigObj } = useSignupConfigV2([SignupInputFieldName.Username]);
    const showChatServiceButton =
        COMMON_SITE_CONFIGS.chatService.enable && !COMMON_SITE_CONFIGS.chatService.showAfterSignIn;

    const onChatServiceClick = useCallback(() => {
        showWidget();
    }, [showWidget]);

    return (
        <>
            <Button
                type="button"
                onClick={onWalletOpen}
                variant="custom"
                className={styles.walletButton}
                data-testid="open-wallet-button">
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/wallet.svg`}
                    className="svg-icon"
                />
            </Button>

            <div className={styles.headerButtons}>
                <ActiveLink
                    activeClassName={styles.active}
                    href={`/${RouteNames.ACCOUNT}/${RouteNames.BETS}/${RouteNames.SPORT}`}>
                    <Svg
                        src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-my-bets.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                </ActiveLink>
                {messagesConfig?.enable && (
                    <ActiveLink
                        activeClassName={styles.active}
                        href={`/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.INBOX}`}>
                        <Svg
                            src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages.svg`}
                            className="svg-icon"
                            prefixAppName
                        />
                    </ActiveLink>
                )}
                {!!referralConfig && (
                    <ActiveLink
                        activeClassName={styles.active}
                        href={`/${RouteNames.ACCOUNT}/${RouteNames.REFERRAL}/${RouteNames.SPORT}`}>
                        <Svg
                            src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-referral.svg`}
                            className="svg-icon"
                            prefixAppName
                        />
                    </ActiveLink>
                )}

                {showChatServiceButton && (
                    <Button
                        type="button"
                        variant="custom"
                        disabled={visible}
                        onClick={onChatServiceClick}>
                        <Svg
                            src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-livechat.svg`}
                            className="svg-icon"
                            prefixAppName
                        />
                    </Button>
                )}
                {COMMON_SITE_CONFIGS.crm.enable && (
                    <Button type="button" variant="custom" onClick={() => openInbox()}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/notifications.svg`}
                            className="svg-icon"
                        />
                        {unreadMessagesCount > 0 && (
                            <i className={styles.notificationsCount}>{unreadMessagesCount}</i>
                        )}
                    </Button>
                )}

                <Popover
                    placement="bottom"
                    className={styles.headerAccountPopover}
                    maxHeight="70vh"
                    trigger={
                        <>
                            <span>{signupConfigObj.username ? username : userEmail}</span>

                            <Button
                                type="button"
                                ref={popoverTriggerRef}
                                variant="custom"
                                className={classNames(
                                    styles.accountButton,
                                    pathnameValues?.includes(RouteNames.ACCOUNT) &&
                                        styles.accountButtonActive,
                                )}
                                data-testid="open-user-panel-button">
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/menu-login-icon.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                {!isUserVerified && (
                                    <Svg
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`}
                                        className="svg-icon-unverified"
                                    />
                                )}
                            </Button>
                        </>
                    }>
                    <AccountPopover onItemClick={() => popoverTriggerRef.current?.click()} />
                </Popover>
            </div>
        </>
    );
};

type Props = {
    advancedSidebarModalRef: RefObject<IModalForwardRefPropsV2 | null>;
};

const Tablet: FC<Props> = ({ advancedSidebarModalRef }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const { headerRef } = useContext(HeaderContext);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { currentModal, loginModalRef, signupModalRef, setCurrentModal } =
        useContext(ModalsContextV2);

    const onSignupPress = useCallback(() => {
        if (currentModal === ModalTypes.SIGNUP) {
            setCurrentModal(null);
        } else {
            signupModalRef.current?.open("opacity");
        }
    }, [currentModal, setCurrentModal, signupModalRef]);

    const onToggleSidebarClick = () => {
        if (advancedSidebarModalRef.current) {
            if (advancedSidebarModalRef.current.isOpened) {
                setCurrentModal(null);
            } else {
                advancedSidebarModalRef.current.open();
            }
        }
    };

    return (
        <header className={styles.header} ref={headerRef}>
            <Button
                type="button"
                variant="custom"
                className={classNames(styles.collapseBtn, {
                    [styles.opened]: currentModal === ModalTypes.ADVANCED_SIDEBAR,
                })}
                onClick={onToggleSidebarClick}>
                <span>
                    <i />
                    <span />
                    <span />
                    <span />
                </span>
            </Button>
            <Link href="/">
                <Img
                    source={`/${AssetsDirectory.Mobile}/logo.svg`}
                    alt={`${COMMON_SITE_CONFIGS.appName} logo`}
                    title={`${COMMON_SITE_CONFIGS.appName} logo`}
                    width={0}
                    height={0}
                    style={{ width: "100%", height: "auto" }}
                    prefixAppName
                />
            </Link>
            <HeaderNav />
            <ErrorBoundary name={WalletSelector.name}>
                <WalletSelector />
            </ErrorBoundary>
            {isUserLoggedIn ? (
                <UserPanel />
            ) : (
                <>
                    <Button
                        type="button"
                        onClick={() => loginModalRef.current?.open()}
                        disabled={currentModal === ModalTypes.LOGIN}
                        data-testid="header-login-button"
                        className={styles.loginBtn}>
                        <Translate tid="header_login" />
                    </Button>
                    <Button
                        data-testid="signupButton"
                        type="button"
                        onClick={onSignupPress}
                        disabled={currentModal === ModalTypes.SIGNUP}
                        variant="primary"
                        className={styles.registerBtn}>
                        <Translate tid="header_registerNow" />
                    </Button>
                    <CpfSignupProvider>
                        <SignupModal />
                    </CpfSignupProvider>
                </>
            )}
        </header>
    );
};

export default Tablet;
